import { useAnalytics } from '@/hooks/useAnalytics';
import allSchoolFlyersIllustration from '@/assets/all-school-illustration.svg';
import allCommunityFlyersIllustration from '@/assets/all-community-illustration.svg';
import { BodyMedium, TitleSmallEmphasized } from '@/design-system';
import colors from '@/theme/colors';
import styles from './AllFlyersBanner.styles';
import { AccessibleDivButton, FadeInWrapper } from '@/components';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { useReactiveVar } from '@apollo/client';
import { districtVar, subscriptionsVar } from '@/graphql/state';
import { TrackingFrom, useNavigateWithTracking, useSchools } from '@/hooks';
import { APP_ROUTES, FlyerType, SubscriptionTypeEnum } from '@/config/constants';

interface Props {
  activeTab: string;
  isSchoolTab: boolean;
  trackingFrom: TrackingFrom;
}

const AllFlyersBanner: React.FC<Props> = ({ activeTab, isSchoolTab, trackingFrom }) => {
  const { track } = useAnalytics();
  const district = useReactiveVar(districtVar);
  const navigateWithTracking = useNavigateWithTracking();
  const { hasMultipleChildren, mySchoolId, subscriptions } = useSchools();

  const description = `Explore all ${activeTab} flyers`;

  const handleClick = () => {
    if (subscriptions.length === 0) return;

    track({
      category: `explore_page:all_flyers:banners`,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label: description,
    });

    let flyerboardParams = '';
    if (isSchoolTab) {
      flyerboardParams = `audienceId=${
        hasMultipleChildren ? mySchoolId : subscriptions[0].id
      }&audienceType=${activeTab}&tab=${activeTab}`;
    } else {
      flyerboardParams = `audienceId=${district?.districtId}&audienceType=${SubscriptionTypeEnum.district}&tab=${activeTab}`;
    }

    navigateWithTracking(`${APP_ROUTES.exploreAllFlyers}?${flyerboardParams}`, trackingFrom);
  };

  return (
    <AccessibleDivButton
      className={styles.container}
      onClick={handleClick}
      data-testid="AllFlyersBanner"
    >
      <img
        src={isSchoolTab ? allSchoolFlyersIllustration : allCommunityFlyersIllustration}
        width="72px"
        height="72px"
        alt=""
      />
      <TitleSmallEmphasized aria-hidden="true">All</TitleSmallEmphasized>
      <BodyMedium color={colors.slate}>{description}</BodyMedium>
    </AccessibleDivButton>
  );
};

export default AllFlyersBanner;
